import { CommonModule, Location } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatTabsModule } from "@angular/material/tabs";
import { ActivatedRoute, Router, RouterLink, UrlTree } from "@angular/router";
import { Subscription } from "rxjs";

import { guardarDescarga, mensajeDeDescarga } from "src/app/bsc/bsc-descarga";
import {
  AnalisisResumen,
  CalidadCodigoRes,
  Condicion,
  EstadoCondicionCc,
  EstadoGateCc,
  EventoAnalisis,
  ProyectoDetalle,
  ReporteEmitido,
  SeriesHistorial,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CalidadCodigoService } from "../calidad-codigo.service";
import {
  CATEGORIA_EVENTO,
  ESTADO_PROYECTO,
  EstadoCc,
  estadoDe,
  textoDeError,
} from "../cc-estados";
import {
  SIN_DATO,
  aFecha,
  commitCorto,
  entero,
  fechaCorta,
  fechaHora,
  haceCuanto,
  letra,
  mayuscula,
  nombreMetrica,
  nombres,
  nombrePeriodo,
  numero,
  pct,
  plural,
  textoUmbral,
  textoVersion,
  tipoMetrica,
} from "../cc-formato";
import { ajustePaginado, cuentaEstable, numerosDePagina, textoRango } from "../cc-paginado";
import { RUTA_TABLERO, destinoDeRegreso } from "../cc-regreso";
import { AnilloCcComponent, TonoAnilloCc, tonoCobertura, tonoDuplicacion } from "../graficas/anillo-cc.component";
import { CifraCcComponent, textoCifra } from "../graficas/cifra-cc.component";
import { IconoCcComponent } from "../graficas/icono-cc.component";
import { CambioSerie, FormatoSerieCc, PuntoSerieCc, SentidoMetaCc, cambioDesdeElPrimero } from "../graficas/serie-cc-lectura";
import { SerieCcComponent } from "../graficas/serie-cc.component";
import { FlotantesCcService } from "../flotantes/flotantes-cc.service";
import { AvisoEmisionCc, EMITIENDO_EXCEL, EMITIENDO_PDF, avisoDeEmision, avisoDeError } from "../reporte/actualizacion-cc";
import { descargarPdfProyecto } from "../reporte/pdf-proyecto";

/**
 * Lo que dice el resultado de una cifra, en palabras de cualquiera:
 * «Cumple», «No cumple», «Sin meta» (el gate no la pide)…
 */
interface Resultado {
  texto: string;
  clase: string;
  icono: string;
  /** Lo que significa, para el title. */
  titulo: string;
}

/** Un trozo de un texto armado; las cifras van con su peso (cc-num) y, si lo trae, con su title. */
interface Trozo {
  texto: string;
  cifra: boolean;
  titulo?: string;
}

/**
 * Una cifra del Resumen con su resultado contra la meta del Quality Gate: la cifra grande de un mosaico o una de sus
 * partes («Condiciones», «Revisados», las calificaciones).
 */
interface FilaResumen {
  nombre: string;
  /** Qué mide una parte, para el title de su nombre: "Riesgo de ataques". */
  ayuda: string;
  resultado: Resultado;
  falla: boolean;
  /** El valor como texto («—», «Nada que medir»); las cifras con conteo van en `cifra`. */
  valor: string;
  /** La cifra con conteo: PCT con un decimal, PCT2 con hasta dos (la duplicación, como SonarQube: "0.34 %"). */
  cifra: { valor: number; formato: "PCT" | "PCT2" | "ENTERO" } | null;
  letra: string;
  /** La calificación en palabras: "la mejor" (A) … "la peor" (E). */
  letraTexto: string;
  /** La base de la cifra: "Sobre 8,268 líneas nuevas por cubrir", "156 líneas repetidas". */
  detalle: string;
  anillo: { valor: number | null; tono: TonoAnilloCc; pistaConTono: boolean } | null;
  /** "Meta: 95 % o más"; vacío si el gate no la pide. */
  meta: string;
  desde: string;
  nota: string;
  /** La nota completa, para su title. */
  notaTitulo: string;
  /** La lista de SonarQube de esta cifra; vacío si el servidor no la manda (la cifra va sin enlace). */
  enlace: string;
  /** Qué abre el enlace, para su title: "Abrir en SonarQube los hallazgos nuevos". */
  enlaceTitulo: string;
  /** Una parte sin cifra ni meta: dice solo «Sin dato», sin la raya ni el «Sin meta» de al lado. */
  sinResultado: boolean;
}

/** Una cifra chica con su enlace: "10 de seguridad", "48 bloqueantes", "191 falsas alarmas". */
interface Enlazado {
  cifra: string;
  texto: string;
  enlace: string;
  titulo: string;
}

/** Un mosaico del Resumen, como los de SonarQube: qué mide, la cifra grande, su meta, su base y sus partes. */
interface Mosaico {
  clave: string;
  nombre: string;
  /** Qué mide, para quien no conoce SonarQube: "Código que se prueba". */
  explica: string;
  /** La cifra grande con su resultado; null en «Calificaciones», que solo trae sus partes. */
  fila: FilaResumen | null;
  /** Lo que cuenta la cifra grande, debajo de ella: "hallazgos abiertos". */
  unidad: string;
  /** Lo que cuenta la cifra grande, a su lado (cabe en el mismo renglón): "por revisar". */
  sufijo: string;
  /** Las cifras chicas con su enlace: de qué tipo son los hallazgos, cuántos son graves, las falsas alarmas. */
  tipos: Enlazado[];
  partes: FilaResumen[];
  /** No cumple la cifra grande o alguna de sus partes: el mosaico va en rojo. */
  falla: boolean;
}

/** Las dos pestañas del Resumen, como «New Code» y «Overall Code» de SonarQube. */
type VistaResumen = "nuevo" | "general";

/** El enlace de una cifra y lo que abre. */
interface Liga {
  enlace: string;
  titulo: string;
}

/** El resultado según el estado del criterio del gate. */
const RESULTADOS: Record<EstadoCondicionCc, Resultado> = {
  OK: { texto: "Cumple", clase: "is-pasa", icono: "check", titulo: "Cumple la meta del Quality Gate" },
  ERROR: { texto: "No cumple", clase: "is-no-pasa", icono: "close", titulo: "No cumple la meta del Quality Gate" },
  SIN_DATO: { texto: "Sin dato", clase: "is-neutro", icono: "help_outline", titulo: "El análisis no trae esta cifra: no se pudo comparar con la meta" },
  IGNORADA: {
    texto: "No cuenta",
    clase: "is-aviso",
    icono: "visibility_off",
    titulo: "SonarQube no la toma en cuenta: el cambio fue pequeño",
  },
};
const SIN_META: Resultado = { texto: "Sin meta", clase: "is-neutro", icono: "remove", titulo: "El Quality Gate no le pone meta a esta cifra: solo informa" };
/** Lo que dice el title de lo que SonarQube no evaluó: por qué, si el gate cambió después del análisis. */
const POR_GATE_CAMBIADO = "El Quality Gate cambió después de este análisis";

/** Las calificaciones de SonarQube en palabras: A es lo mejor, E lo peor. */
const LETRA_EN_PALABRAS: Record<string, string> = { A: "la mejor", B: "buena", C: "regular", D: "mala", E: "la peor" };
const TITULO_LETRA = "Calificación de A a E: A es lo mejor, E lo peor";

/** Qué mide cada mosaico, en palabras sencillas (breves: junto a su nombre). */
const EXPLICA = {
  hallazgosNuevos: "Problemas en lo que cambió",
  hallazgosAbiertos: "Problemas sin resolver",
  aceptados: "Hallazgos válidos que se decidió no corregir",
  seguridad: "Riesgo de ataques",
  confiabilidad: "Riesgo de fallas",
  mantenibilidad: "Facilidad de cambio",
  calificaciones: "A es la mejor, E la peor",
  cobertura: "Código que se prueba",
  duplicacion: "Código repetido",
  hotspots: "Puntos sensibles",
  pruebas: "Pruebas automáticas",
};

/** Los criterios que no cumplió un análisis, en palabras: "Fallaron 3 criterios" y por grupo, "Código nuevo: hallazgos y cobertura". */
interface CriteriosFallidos {
  rotulo: string;
  grupos: { nombre: string; lista: string }[];
}

/** Un renglón de la tabla del historial. */
interface FilaHistorial {
  id: number;
  fecha: string;
  /** "Hace 2 h", para el title de la fecha. */
  hace: string;
  estado: EstadoCc<EstadoGateCc>;
  /** "Pasó el Quality Gate «BanCoppel»", para el title de la insignia. */
  gateTitulo: string;
  /** "volvió a pasar" o "dejó de pasar": el gate cambió en este análisis (evento de SonarQube). */
  gateCambio: string;
  fallan: CriteriosFallidos | null;
  commit: string;
  commitCompleto: string;
  enlaceCommit: string;
  version: string;
  /** Los eventos del análisis sin el del Quality Gate (va como «dejó de pasar» o «volvió a pasar»): tres a la vista y el resto con «Ver más». */
  eventos: string[];
  borrado: boolean;
  sinCifrasNuevo: boolean;
}

/** Cuántos eventos se enseñan por renglón del historial (una actualización de SonarQube trae hasta nueve). */
const EVENTOS_A_LA_VISTA = 3;

/** El resultado del gate de un análisis, en pasado: ya ocurrió. */
const GATE_DEL_ANALISIS: EstadoCc<EstadoGateCc>[] = [
  { valor: "OK", texto: "Pasó", clase: "is-pasa", icono: "check" },
  { valor: "ERROR", texto: "No pasó", clase: "is-no-pasa", icono: "close" },
  { valor: "NONE", texto: "Sin resultado", clase: "is-neutro", icono: "remove" },
];

/** El estado del último análisis en SonarQube, para su flotante al abrir el proyecto (las cifras son del anterior). */
const CE_EN_PROCESO = "SonarQube está procesando un análisis nuevo; las cifras son del anterior.";
const CE_FALLIDO = "SonarQube no pudo procesar el último análisis; las cifras son del anterior.";

/** Lo que dice el flotante cuando no se pudo pedir el reporte (PDF o Excel). */
const SIN_CONEXION_REPORTE = "No se pudo emitir el reporte: sin conexión con el servidor del COS.";

/** El evento QUALITY_GATE de SonarQube dice a qué cambió el gate en ese análisis. */
const CAMBIO_DE_GATE: Record<string, string> = { pasa: "volvió a pasar", "no pasa": "dejó de pasar" };

/** Una de las tres gráficas del historial, con lo que dice su encabezado. */
interface GraficaHistorial {
  clave: string;
  nombre: string;
  /** Qué mide, en palabras de cualquiera. */
  explica: string;
  /** El color de su línea, un token del tema. */
  tono: string;
  valores: (number | null)[];
  formato: FormatoSerieCc;
  meta: number | null;
  sentido: SentidoMetaCc;
  menosEsMejor: boolean;
  unidad: string;
  ultimo: number | null;
  cambio: CambioSerie;
}

/** Cuántos análisis traen las series (los últimos 20): se pide el detalle de los mismos para sus globos. */
const ANALISIS_EN_SERIES = 20;
/** El aire bajo el Resumen (relleno inferior de la página), en píxeles: la pieza llega justo al final de la ventana. */
const MARGEN_RESUMEN = 12;

/** Las claves con que puede venir una calificación: MQR y clásica. */
interface ClavesLetra {
  mqr: string;
  clasica: string;
  hallazgos: string;
}

const FILAS_HISTORIAL_MIN = 3;
const FILAS_HISTORIAL_MAX = 8;
const ALTO_FILA_HISTORIAL = 44;
/** El aire bajo la tarjeta del historial (relleno de la página de ajustes), en píxeles. */
const MARGEN_HISTORIAL = 30;
/** Lo que dice una cifra que no se mide: no hubo hotspots nuevos o no hubo código nuevo. */
const NADA_QUE_REVISAR = "Nada que revisar";
const NADA_QUE_MEDIR = "Nada que medir";
const SIN_CODIGO_NUEVO = "Sin código nuevo";

/**
 * Calidad de código · Detalle de un proyecto (docs/CALIDAD_CODIGO.md §5):
 * cuelga del tablero (admin/calidad-codigo/proyecto/:id, el id del COS). El
 * encabezado es un solo renglón de botones (ver abajo). La pestaña Resumen es
 * como el resumen de SonarQube: dos pestañas en un control segmentado, «Código
 * nuevo» (por omisión) y «Código general», solo con su nombre; debajo, qué
 * abarca y los mosaicos con su cifra grande, su meta del gate (si cumple y
 * desde cuándo no) y su base, escritos para quien no conoce SonarQube. Cada
 * cifra abre en SonarQube la lista de eso mismo. La pieza llega al final de
 * la ventana con una letra de tamaño fijo; el alto que sobra se reparte en el
 * aire de cada mosaico. La pestaña
 * Historial trae arriba tres gráficas con su color, su meta y un globo por
 * análisis, y abajo la tabla paginada de análisis guardados (incluidos los
 * que SonarQube ya purgó), con los criterios que fallaron completos.
 * El encabezado es un renglón: a la izquierda «Regresar» (vuelve al tablero
 * como se dejó, con sus filtros; cc-regreso.ts) y el nombre del proyecto; a la
 * derecha la ficha de este análisis (solo el commit y la versión) y las salidas
 * (SonarQube, Excel, Reporte PDF). El veredicto del gate, sus criterios y la
 * fecha del análisis se ven en SonarQube y siguen en el PDF y el Excel; el
 * análisis desactualizado y el estado del último análisis salen como flotantes
 * al abrir. A la vista en las dos pestañas.
 *
 * Solo lectura sobre la foto del COS: el servidor resuelve estados, letras,
 * criterios que fallan y «desde cuándo»; aquí se pinta y se anima (anillos,
 * conteos y gráficas de entrada; nada con prefers-reduced-motion).
 *
 * Base visual: admin/ajustes (.ajustes-page) con mat-tab-group, como la
 * ficha de KPI del BSC. «Reporte PDF» y «Excel» emiten un folio (foto
 * congelada, después de que el servidor consulta SonarQube) y descargan el
 * archivo; si la consulta trajo novedades, el proyecto se vuelve a pedir.
 */
@Component({
  selector: "app-detalle-cc",
  standalone: true,
  templateUrl: "./detalle-cc.component.html",
  styleUrls: ["../calidad-codigo.shared.css", "./detalle-cc.component.css"],
  imports: [CommonModule, RouterLink, MatTabsModule, SerieCcComponent, AnilloCcComponent, CifraCcComponent, IconoCcComponent],
})
export class DetalleCcComponent implements OnInit, OnDestroy {
  readonly tituloLetra = TITULO_LETRA;
  /** Los globos de «Reporte PDF» y «Excel» mientras emiten: primero se consulta SonarQube. */
  readonly emitiendoPdf = EMITIENDO_PDF;
  readonly emitiendoExcel = EMITIENDO_EXCEL;
  @ViewChild("tablaHistorial") private readonly tablaHistorial?: ElementRef<HTMLElement>;
  @ViewChild("cuerpoResumen") private readonly cuerpoResumen?: ElementRef<HTMLElement>;

  /**
   * A dónde lleva «Regresar»: el tablero como se dejó, con sus filtros (lo
   * pasa en el state al abrir el proyecto), o el tablero sin filtros si se
   * llegó por otro lado.
   */
  regreso: UrlTree | string = RUTA_TABLERO;
  id: number | null = null;
  loading = false;
  error = "";
  proyecto: ProyectoDetalle | null = null;
  series: SeriesHistorial | null = null;
  estado = estadoDe(ESTADO_PROYECTO, null);
  puedeReportar = false;
  pestana = 0;

  /** La pestaña del Resumen a la vista: «Código nuevo» por omisión, como en SonarQube. */
  vista: VistaResumen = "nuevo";
  /** Los mosaicos de cada pestaña, en hileras: tres y tres en «Código nuevo»; cinco y cuatro en «Código general». */
  mosaicosNuevo: Mosaico[][] = [];
  mosaicosGeneral: Mosaico[][] = [];
  /** Lo que abarca cada pestaña: "Código nuevo desde el 23/02/2026 (versión anterior) · empezó hace 7 meses · 45,771 líneas". */
  abarcaNuevo: Trozo[] = [];
  abarcaGeneral: Trozo[] = [];
  /** El Quality Gate le pone al menos una meta a la pestaña; sin metas, sus cifras no repiten «Sin meta». */
  metasNuevo = false;
  metasGeneral = false;

  /**
   * El alto mínimo del Resumen para que sus tarjetas lleguen al final de la ventana (null hasta medirlo): se mide
   * porque lo que hay arriba cambia con el proyecto (el renglón de botones baja a dos con poco ancho).
   */
  altoResumen: number | null = null;
  /** Con la letra normal la pieza no cabe en la ventana: la letra de los mosaicos baja un paso. */
  resumenCompacto = false;
  graficas: GraficaHistorial[] = [];
  /** Gate, versión y commit de cada análisis de las series, para los globos de las gráficas. */
  puntosSerie: PuntoSerieCc[] = [];
  historial: FilaHistorial[] = [];
  /** Los renglones con todos sus eventos a la vista (por id del análisis). */
  eventosAbiertos = new Set<number>();
  /** El detalle de los análisis de las series (versión y commit), por fecha ISO. */
  private detalles = new Map<string, AnalisisResumen>();
  historialTotal = 0;
  historialPagina = 1;
  historialPorPagina = 6;
  /** El aire que se reparte en cada fila del historial para que la tarjeta llegue al final de la ventana. */
  historialExtra = 0;
  historialCargando = false;
  historialError = "";
  /** La pestaña Historial ya pidió su primera página. */
  private historialPedido = false;
  /** Cuántas veces seguidas cambiaron las filas por página (tope: 3) y si la última vez se redujeron. */
  private ajustesSeguidos = 0;
  private ajusteEncogio = false;

  emitiendo: "PDF" | "EXCEL" | null = null;
  /** El proyecto cuyos mensajes informativos ya salieron: se muestran una vez al abrirlo, no en cada repintado. */
  private avisadoA: number | null = null;
  /** Los mensajes informativos de ese proyecto (gate cambiado, cifras incompletas): se van al dejarlo. */
  private informativos: number[] = [];

  private consulta?: Subscription;
  private consultaHistorial?: Subscription;
  private consultaDetalles?: Subscription;
  private accion?: Subscription;
  private ruta$?: Subscription;
  private esperaAjuste: ReturnType<typeof setTimeout> | null = null;
  private esperaResumen: ReturnType<typeof setTimeout> | null = null;
  private readonly ahora = new Date();

  constructor(
    private readonly servicio: CalidadCodigoService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly cdr: ChangeDetectorRef,
    private readonly flotantes: FlotantesCcService,
  ) {}

  ngOnInit(): void {
    // Angular reutiliza el componente si solo cambia el id de la ruta: se vuelve a consultar.
    this.ruta$ = this.route.paramMap.subscribe((ruta) => {
      // Cada navegación trae su propio state: sin el del tablero, se regresa al tablero sin filtros.
      this.regreso = this.router.parseUrl(destinoDeRegreso(this.location.getState()));
      const id = Number(ruta.get("id"));
      this.id = Number.isInteger(id) && id > 0 ? id : null;
      this.historialPedido = false;
      this.historial = [];
      this.historialPagina = 1;
      this.vista = "nuevo";
      this.detalles = new Map();
      this.eventosAbiertos = new Set();
      if (this.id == null) {
        this.proyecto = null;
        this.error = "El enlace no trae un proyecto válido. Ábrelo desde el tablero.";
        this.cdr.detectChanges();
        return;
      }
      this.consultar();
    });
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.consultaHistorial?.unsubscribe();
    this.consultaDetalles?.unsubscribe();
    this.accion?.unsubscribe();
    this.ruta$?.unsubscribe();
    this.cierraInformativos();
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    if (this.esperaResumen) {
      clearTimeout(this.esperaResumen);
    }
  }

  /* ============================ Consulta ============================ */

  /**
   * `silencioso`: la recarga que sigue a un reporte que trae algo nuevo no enseña «Consultando el proyecto»; lo que se
   * ve se queda hasta que llega lo nuevo.
   */
  consultar(silencioso = false): void {
    if (this.id == null) {
      return;
    }
    const pedido = this.id;
    this.consulta?.unsubscribe();
    this.loading = !silencioso;
    this.error = "";
    this.cdr.detectChanges();
    this.consulta = this.servicio.proyecto(pedido).subscribe({
      next: (res) => {
        if (pedido !== this.id) {
          return;
        }
        this.loading = false;
        if (res?.codigoError || !res?.proyecto) {
          this.error = res?.codigoError ? textoDeError(res, "consultar el proyecto") : "El proyecto no está en el COS.";
          this.proyecto = null;
        } else {
          this.aplica(res);
        }
        this.cdr.detectChanges();
        this.avisaAlAbrir();
        this.programaResumen();
      },
      error: () => {
        if (pedido !== this.id) {
          return;
        }
        this.loading = false;
        this.error = "No se pudo consultar el proyecto: sin conexión con el servidor del COS.";
        this.proyecto = null;
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: CalidadCodigoRes): void {
    const p = res.proyecto as ProyectoDetalle;
    this.proyecto = p;
    this.series = res.series ?? null;
    this.puedeReportar = res.puedeReportar === true;
    this.estado = estadoDe(ESTADO_PROYECTO, p.estado);
    const m = p.medidas ?? {};
    const condiciones = p.condiciones ?? [];
    this.mosaicosNuevo = aclaraSinEvaluar(this.armaNuevo(m, condiciones, p), p.gateCambio);
    this.mosaicosGeneral = aclaraSinEvaluar(this.armaGeneral(m, condiciones, p), p.gateCambio);
    this.abarcaNuevo = this.armaAbarcaNuevo(m, p);
    this.abarcaGeneral = armaAbarcaGeneral(m);
    this.metasNuevo = condiciones.some((c) => c.codigoNuevo === true);
    this.metasGeneral = condiciones.some((c) => c.codigoNuevo === false);
    this.armaSeries();
  }

  /* ============================ Encabezado ============================ */

  get titulo(): string {
    return this.proyecto?.nombre || "Proyecto";
  }

  /**
   * Desde cuándo cuenta el código nuevo, para su pestaña: «23/02/2026» (con la hora para su title), « (versión
   * anterior)» y hace cuánto empezó, «hace 7 meses»; null sin periodo.
   */
  get cuandoPeriodo(): { fecha: string; fechaHora: string; tipo: string; hace: string } | null {
    const per = this.proyecto?.periodo;
    if (!per?.fecha) {
      return null;
    }
    return {
      fecha: fechaCorta(per.fecha),
      fechaHora: fechaHora(per.fecha),
      tipo: per.tipo ? ` (${nombrePeriodo(per.tipo, per.valor)})` : "",
      hace: empezoHace(per.fecha, this.ahora),
    };
  }

  /** La versión del proyecto (dato «Versión» de la ficha del análisis); vacío sin dato o con el «not provided» de SonarQube. */
  get versionTexto(): string {
    return textoVersion(this.proyecto?.versionProyecto);
  }

  /**
   * Los mensajes informativos del análisis, como flotantes y una sola vez al abrir el proyecto (no en cada repintado
   * ni al cambiar de pestaña): el gate cambió después del análisis, las cifras del código nuevo incompletas y, desde
   * que el encabezado es solo un renglón de botones, el análisis desactualizado y el estado del último análisis en
   * SonarQube (en proceso o fallido).
   */
  private avisaAlAbrir(): void {
    const p = this.proyecto;
    if (!p || p.id === this.avisadoA) {
      return;
    }
    this.cierraInformativos();
    this.avisadoA = p.id;
    if (p.gateCambio) {
      this.informativos.push(
        this.flotantes.info("Los criterios del Quality Gate cambiaron después de este análisis: el próximo se evaluará con los nuevos.", {
          icono: "published_with_changes",
        }),
      );
    }
    if (p.cifrasNuevoCompletas === false) {
      this.informativos.push(
        this.flotantes.aviso("Las cifras del código nuevo de este análisis no están completas: SonarQube ya tenía uno más nuevo cuando el COS lo guardó."),
      );
    }
    if (p.desactualizado) {
      this.informativos.push(this.flotantes.aviso("Este proyecto lleva más días sin analizarse que el umbral configurado.", { icono: "schedule" }));
    }
    if (p.ceEstado === "EN_PROCESO") {
      this.informativos.push(this.flotantes.info(CE_EN_PROCESO, { icono: "hourglass_top" }));
    }
    if (p.ceEstado === "FALLIDO") {
      const texto = p.ceError ? `El último análisis falló en SonarQube: ${p.ceError}` : CE_FALLIDO;
      this.informativos.push(this.flotantes.aviso(texto, { icono: "warning_amber" }));
    }
  }

  private cierraInformativos(): void {
    for (const id of this.informativos) {
      this.flotantes.cierra(id);
    }
    this.informativos = [];
  }

  /** El commit corto del análisis (dato «Commit» de la ficha); vacío sin dato. */
  get commit(): string {
    return commitCorto(this.proyecto?.revision);
  }

  /* ============================ Resumen ============================ */

  /**
   * Cambia la pestaña del Resumen (clic o teclado). Si la nueva no cabe con la letra normal, la letra baja un paso y
   * así se queda en las dos pestañas (no vuelve a crecer al regresar).
   */
  eligeVista(vista: VistaResumen): void {
    this.vista = vista;
    this.cdr.detectChanges();
    this.ajustaResumen(false);
  }

  /**
   * Las flechas izquierda y derecha (y Inicio y Fin) pasan de una pestaña a la otra y llevan el foco a la elegida,
   * como pide el patrón de pestañas de WAI-ARIA (activación automática).
   */
  teclaVista(evento: KeyboardEvent): void {
    const destino = destinoDeTecla(evento.key, this.vista);
    if (!destino) {
      return;
    }
    evento.preventDefault();
    this.eligeVista(destino);
    const lista = (evento.currentTarget as HTMLElement | null)?.closest("[role=tablist]");
    lista?.querySelector<HTMLElement>(`#cc-det-solapa-${destino}`)?.focus();
  }

  /**
   * «Código nuevo», como el de SonarQube: hallazgos nuevos (y de qué tipo), aceptados y cobertura; duplicación,
   * hotspots de seguridad y las tres calificaciones.
   */
  private armaNuevo(m: Record<string, string>, cond: Condicion[], p: ProyectoDetalle): Mosaico[][] {
    const e = p.enlaces;
    const sinNuevo = p.sinCodigoNuevo;
    const tipos = [
      enlazado(m["new_software_quality_security_issues"], DE_SEGURIDAD, liga(e?.hallazgosNuevosSeguridad, "los hallazgos nuevos de seguridad")),
      enlazado(m["new_software_quality_reliability_issues"], DE_CONFIABILIDAD, liga(e?.hallazgosNuevosConfiabilidad, "los hallazgos nuevos de confiabilidad")),
      enlazado(m["new_software_quality_maintainability_issues"], DE_MANTENIBILIDAD, liga(e?.hallazgosNuevosMantenibilidad, "los hallazgos nuevos de mantenibilidad")),
    ].filter((t): t is Enlazado => t != null);
    const cobertura = this.filaPct("Cobertura", "new_coverage", m, cond, sinNuevo, liga(e?.coberturaNueva, "la cobertura del código nuevo, archivo por archivo"));
    cobertura.detalle = sinNuevo ? "" : sobre(m["new_lines_to_cover"], "línea nueva por cubrir", "líneas nuevas por cubrir");
    const condiciones = this.filaPct(
      "Condiciones",
      "new_branch_coverage",
      m,
      cond,
      sinNuevo,
      liga(e?.coberturaCondicionesNueva, "la cobertura de condiciones del código nuevo, archivo por archivo"),
    );
    condiciones.anillo = null;
    condiciones.ayuda = "Decisiones sí/no del código nuevo que revisan las pruebas";
    const duplicacion = this.filaPct("Duplicación", "new_duplicated_lines_density", m, cond, sinNuevo, liga(e?.duplicacionNueva, "la duplicación del código nuevo, archivo por archivo"));
    duplicacion.detalle = sinNuevo ? "" : repetidas(m["new_duplicated_lines"]);
    const calificaciones = [
      this.filaLetra("Seguridad", { mqr: "new_software_quality_security_rating", clasica: "new_security_rating", hallazgos: "new_software_quality_security_issues" }, m, cond, p),
      this.filaLetra("Confiabilidad", { mqr: "new_software_quality_reliability_rating", clasica: "new_reliability_rating", hallazgos: "new_software_quality_reliability_issues" }, m, cond, p),
      this.filaLetra("Mantenibilidad", { mqr: "new_software_quality_maintainability_rating", clasica: "new_maintainability_rating", hallazgos: "new_software_quality_maintainability_issues" }, m, cond, p),
    ];
    [EXPLICA.seguridad, EXPLICA.confiabilidad, EXPLICA.mantenibilidad].forEach((ayuda, i) => (calificaciones[i].ayuda = ayuda));
    return [
      [
        mosaico("hallazgos-nuevos", "Hallazgos nuevos", EXPLICA.hallazgosNuevos, this.filaConteo("Hallazgos nuevos", "new_violations", m, cond, liga(e?.hallazgosNuevos, "los hallazgos nuevos")), { tipos }),
        mosaico("aceptados-nuevos", "Aceptados", EXPLICA.aceptados, this.filaConteo("Aceptados", "new_accepted_issues", m, cond, liga(e?.aceptadosNuevos, "los hallazgos aceptados del código nuevo"))),
        mosaico("cobertura-nueva", "Cobertura", EXPLICA.cobertura, cobertura, { partes: [condiciones] }),
      ],
      [
        mosaico("duplicacion-nueva", "Duplicación", EXPLICA.duplicacion, duplicacion),
        this.mosaicoHotspots(m, cond, true, liga(e?.hotspotsNuevos, "los hotspots de seguridad del código nuevo")),
        mosaico("calificaciones-nuevas", "Calificaciones", EXPLICA.calificaciones, null, { partes: calificaciones }),
      ],
    ];
  }

  /**
   * «Código general», como el de SonarQube: hallazgos abiertos, seguridad, confiabilidad, mantenibilidad y
   * aceptados; cobertura, duplicación, hotspots de seguridad y pruebas unitarias.
   */
  private armaGeneral(m: Record<string, string>, cond: Condicion[], p: ProyectoDetalle): Mosaico[][] {
    const e = p.enlaces;
    const severidad = [
      enlazado(m["software_quality_blocker_issues"], ["bloqueante", "bloqueantes"], liga(e?.hallazgosBloqueantes, "los hallazgos abiertos bloqueantes")),
      enlazado(m["software_quality_high_issues"], ["grave", "graves"], liga(e?.hallazgosGraves, "los hallazgos abiertos graves")),
    ].filter((t): t is Enlazado => t != null);
    const falsas = enlazado(m["false_positive_issues"], ["falsa alarma", "falsas alarmas"], liga(e?.falsasAlarmas, "los hallazgos marcados como falsa alarma"));
    const cobertura = this.filaPct("Cobertura", "coverage", m, cond, false, liga(e?.cobertura, "la cobertura del proyecto, archivo por archivo"));
    cobertura.detalle = sobre(m["lines_to_cover"], "línea por cubrir", "líneas por cubrir");
    const duplicacion = this.filaPct("Duplicación", "duplicated_lines_density", m, cond, false, liga(e?.duplicacion, "la duplicación del proyecto, archivo por archivo"));
    duplicacion.detalle = repetidas(m["duplicated_lines"]);
    return [
      [
        mosaico("hallazgos-abiertos", "Hallazgos abiertos", EXPLICA.hallazgosAbiertos, this.filaConteo("Hallazgos abiertos", "violations", m, cond, liga(e?.hallazgosAbiertos, "los hallazgos abiertos")), { tipos: severidad }),
        this.mosaicoCalidad(m, cond, p, {
          clave: "seguridad",
          nombre: "Seguridad",
          explica: EXPLICA.seguridad,
          claves: { mqr: "software_quality_security_rating", clasica: "security_rating", hallazgos: "software_quality_security_issues" },
          liga: liga(e?.hallazgosSeguridad, "los hallazgos abiertos de seguridad"),
        }),
        this.mosaicoCalidad(m, cond, p, {
          clave: "confiabilidad",
          nombre: "Confiabilidad",
          explica: EXPLICA.confiabilidad,
          claves: { mqr: "software_quality_reliability_rating", clasica: "reliability_rating", hallazgos: "software_quality_reliability_issues" },
          liga: liga(e?.hallazgosConfiabilidad, "los hallazgos abiertos de confiabilidad"),
        }),
        this.mosaicoCalidad(m, cond, p, {
          clave: "mantenibilidad",
          nombre: "Mantenibilidad",
          explica: EXPLICA.mantenibilidad,
          claves: { mqr: "software_quality_maintainability_rating", clasica: "sqale_rating", hallazgos: "software_quality_maintainability_issues" },
          liga: liga(e?.hallazgosMantenibilidad, "los hallazgos abiertos de mantenibilidad"),
        }),
        mosaico("aceptados", "Aceptados", EXPLICA.aceptados, this.filaConteo("Aceptados", "accepted_issues", m, cond, liga(e?.aceptados, "los hallazgos aceptados")), {
          tipos: falsas ? [falsas] : [],
        }),
      ],
      [
        mosaico("cobertura", "Cobertura", EXPLICA.cobertura, cobertura),
        mosaico("duplicacion", "Duplicación", EXPLICA.duplicacion, duplicacion),
        this.mosaicoHotspots(m, cond, false, liga(e?.hotspots, "los hotspots de seguridad")),
        this.mosaicoPruebas(m, cond, liga(e?.pruebas, "las pruebas unitarias, archivo por archivo")),
      ],
    ];
  }

  /**
   * "Código nuevo desde el 23/02/2026 (versión anterior) · empezó hace 7 meses · 45,771 líneas": qué abarca la
   * pestaña, con la fecha desde la que cuenta (la hora va en su title; se dice aquí y no en el encabezado, para no
   * decirla dos veces).
   */
  private armaAbarcaNuevo(m: Record<string, string>, p: ProyectoDetalle): Trozo[] {
    const per = this.cuandoPeriodo;
    const lineas = numero(m["new_lines"]);
    if (!per && (p.sinCodigoNuevo || lineas == null)) {
      return [texto(SIN_CODIGO_NUEVO)];
    }
    const trozos = [texto(p.sinCodigoNuevo ? SIN_CODIGO_NUEVO : "Código nuevo")];
    if (per) {
      trozos.push(texto(" desde el "), { texto: per.fecha, cifra: true, titulo: per.fechaHora }, texto(per.tipo));
      if (per.hace) {
        trozos.push(texto(" · empezó "), cifra(per.hace));
      }
    }
    if (!p.sinCodigoNuevo && lineas != null) {
      trozos.push(texto(" · "), cifra(entero(lineas)), texto(lineas === 1 ? " línea" : " líneas"));
    }
    return trozos;
  }

  /** La base de una cifra: si el gate la pide, su meta, si falla y desde cuándo. */
  private base(nombre: string, c: Condicion | undefined): FilaResumen {
    const falla = c?.estado === "ERROR";
    return {
      nombre,
      ayuda: "",
      resultado: c ? resultadoDe(c.estado) : SIN_META,
      falla,
      valor: "",
      cifra: null,
      letra: "",
      letraTexto: "",
      detalle: "",
      anillo: null,
      meta: c ? metaDe(c) : "",
      desde: falla ? desdeTexto(c) : "",
      nota: c?.estado === "IGNORADA" ? "Ignorada: cambio pequeño" : "",
      notaTitulo: c?.estado === "IGNORADA" ? "SonarQube no cuenta este criterio cuando el cambio es pequeño" : "",
      enlace: "",
      enlaceTitulo: "",
      sinResultado: false,
    };
  }

  private condicion(cond: Condicion[], ...claves: string[]): Condicion | undefined {
    return cond.find((c) => claves.includes(c.metrica));
  }

  /** Un conteo («Hallazgos nuevos», «Aceptados»): su cifra y su enlace. */
  private filaConteo(nombre: string, clave: string, m: Record<string, string>, cond: Condicion[], l: Liga): FilaResumen {
    const c = this.condicion(cond, clave);
    const f = this.base(nombre, c);
    const n = numero(m[clave] ?? c?.valor);
    f.cifra = n == null ? null : { valor: n, formato: "ENTERO" };
    f.valor = n == null ? SIN_DATO : "";
    enlaza(f, l, c);
    return f;
  }

  /**
   * Una calificación de A a E con sus palabras («la mejor» … «la peor»). El panel manda la letra MQR; la clásica,
   * que es la que evalúa el gate, sale del criterio si el panel no la trae. Si el gate la pide y las dos difieren, se
   * enseña la que se compara con la meta (si no, «No cumple» junto a una A no se entendería) y la nota dice la que se
   * ve en SonarQube.
   */
  private filaLetra(nombre: string, claves: ClavesLetra, m: Record<string, string>, cond: Condicion[], p: ProyectoDetalle): FilaResumen {
    const c = this.condicion(cond, claves.mqr, claves.clasica);
    const f = this.base(nombre, c);
    const mqr = letra(m[claves.mqr] ?? m[claves.clasica], "");
    const clasica = letra(m[claves.clasica] ?? (c?.metrica === claves.clasica ? c.valor : null), "");
    const difieren = c != null && clasica !== "" && mqr !== "" && clasica !== mqr;
    const vista = difieren || mqr === "" ? clasica : mqr;
    f.letra = vista || SIN_DATO;
    f.letraTexto = LETRA_EN_PALABRAS[vista] ?? "";
    if (difieren) {
      f.nota = `SonarQube muestra ${mqr}`;
      f.notaTitulo = `SonarQube muestra la calificación nueva (MQR): ${mqr}; el Quality Gate «${p.gateNombre ?? ""}» mide la clásica: ${clasica}`;
    }
    return f;
  }

  /**
   * Seguridad, confiabilidad o mantenibilidad del código general, como en SonarQube: cuántos hallazgos abiertos
   * tiene (con su enlace) y su calificación, con la meta del gate.
   */
  private mosaicoCalidad(
    m: Record<string, string>,
    cond: Condicion[],
    p: ProyectoDetalle,
    q: { clave: string; nombre: string; explica: string; claves: ClavesLetra; liga: Liga },
  ): Mosaico {
    const f = this.filaLetra(q.nombre, q.claves, m, cond, p);
    const n = numero(m[q.claves.hallazgos]);
    f.cifra = n == null ? null : { valor: n, formato: "ENTERO" };
    f.valor = n == null ? SIN_DATO : "";
    enlaza(f, q.liga, undefined);
    const unidad = n === 1 ? "hallazgo abierto" : "hallazgos abiertos";
    return mosaico(q.clave, q.nombre, q.explica, f, { unidad: n == null ? "" : unidad });
  }

  /** El valor sale de las medidas del panel; si el panel no trae esa métrica, del criterio del gate (que sí la evaluó). */
  private filaPct(nombre: string, clave: string, m: Record<string, string>, cond: Condicion[], sinNuevo: boolean, l: Liga): FilaResumen {
    const c = this.condicion(cond, clave);
    const f = this.base(nombre, c);
    const n = numero(m[clave] ?? c?.valor);
    if (sinNuevo || n == null) {
      f.valor = sinNuevo ? NADA_QUE_MEDIR : SIN_DATO;
      return sinEvaluar(f, c, sinNuevo ? "No hubo código nuevo que medir" : "");
    }
    const esDuplicacion = clave.includes("duplicated");
    f.cifra = { valor: n, formato: esDuplicacion ? "PCT2" : "PCT" };
    // La cobertura y la duplicación van en verde, amarillo o rojo según su porcentaje, con la pista del mismo color.
    f.anillo = {
      valor: esDuplicacion ? Math.min(100, n) : n,
      tono: esDuplicacion ? tonoDuplicacion(n) : tonoCobertura(n),
      pistaConTono: true,
    };
    enlaza(f, l, c);
    return f;
  }

  /**
   * Los hotspots de seguridad, como en SonarQube: cuántos hay por revisar (con su enlace) y su calificación; abajo,
   * qué parte ya se revisó, con la meta del gate. En el código nuevo, sin hotspots nuevos no hay nada que revisar.
   */
  private mosaicoHotspots(m: Record<string, string>, cond: Condicion[], nuevo: boolean, l: Liga): Mosaico {
    const prefijo = nuevo ? "new_" : "";
    const claveConteo = `${prefijo}security_hotspots`;
    const claveRevisados = `${prefijo}security_hotspots_reviewed`;
    const cuenta = this.condicion(cond, claveConteo);
    const f = this.base("Hotspots de seguridad", cuenta);
    const pendientes = numero(m[claveConteo]);
    f.cifra = pendientes == null ? null : { valor: pendientes, formato: "ENTERO" };
    f.valor = pendientes == null ? SIN_DATO : "";
    const calificacion = letra(m[`${prefijo}security_review_rating`], "");
    f.letra = calificacion;
    f.letraTexto = LETRA_EN_PALABRAS[calificacion] ?? "";
    enlaza(f, l, cuenta);
    const c = this.condicion(cond, claveRevisados);
    const revisados = this.base("Revisados", c);
    revisados.ayuda = "Parte de los hotspots que alguien ya revisó";
    const n = numero(m[claveRevisados] ?? c?.valor);
    if (nuevo && (pendientes == null || pendientes === 0 || n == null)) {
      revisados.valor = NADA_QUE_REVISAR;
      sinEvaluar(revisados, c, "No hubo hotspots nuevos que revisar");
    } else if (n == null) {
      revisados.valor = SIN_DATO;
    } else {
      revisados.cifra = { valor: n, formato: "PCT" };
    }
    return mosaico(nuevo ? "hotspots-nuevos" : "hotspots", "Hotspots de seguridad", EXPLICA.hotspots, f, {
      sufijo: pendientes == null ? "" : "por revisar",
      partes: [revisados],
    });
  }

  /**
   * Las pruebas unitarias: cuántas hay (con su enlace) y, justo debajo, cuántas se omitieron y su éxito; abajo, el
   * resultado y la meta del gate, para que no se confunda el valor con la meta.
   */
  private mosaicoPruebas(m: Record<string, string>, cond: Condicion[], l: Liga): Mosaico {
    const omitidas = this.condicion(cond, "skipped_tests");
    const exito = this.condicion(cond, "test_success_density");
    const f = this.base("Pruebas unitarias", omitidas ?? exito);
    const pruebas = numero(m["tests"]);
    if (pruebas == null) {
      f.valor = "Sin pruebas";
      f.detalle = "el análisis no trae su reporte";
      return mosaico("pruebas", "Pruebas unitarias", EXPLICA.pruebas, sinEvaluar(f, omitidas ?? exito, ""));
    }
    f.cifra = { valor: pruebas, formato: "ENTERO" };
    const unidad = `${entero(m["skipped_tests"], "0")} omitidas · ${pct(m["test_success_density"], "sin dato de")} éxito`;
    enlaza(f, l, omitidas ?? exito);
    if (omitidas && exito) {
      f.meta = `Meta: ${umbralLlano(omitidas)} omitidas y ${umbralLlano(exito)} de éxito`;
      f.falla = omitidas.estado === "ERROR" || exito.estado === "ERROR";
      f.resultado = resultadoDe(estadoPruebas(f.falla, omitidas.estado === "OK" && exito.estado === "OK"));
      const queFalla = omitidas.estado === "ERROR" ? omitidas : exito;
      f.desde = f.falla ? desdeTexto(queFalla) : "";
    }
    return mosaico("pruebas", "Pruebas unitarias", EXPLICA.pruebas, f, { unidad });
  }

  trackMosaico(_: number, m: Mosaico): string {
    return m.clave;
  }

  /* ============================ Historial ============================ */

  /** Arma lo que sale de las series: los puntos de los globos y las tres gráficas. */
  private armaSeries(): void {
    const s = this.series;
    const fechas = s?.fechas ?? [];
    const estados = s?.estados ?? [];
    this.puntosSerie = fechas.map((f, i) => {
      const a = this.detalles.get(f);
      return { gate: estados[i] ?? "", version: textoVersion(a?.versionProyecto), commit: commitCorto(a?.revision) };
    });
    this.graficas = s ? graficasDe(s) : [];
  }

  /** El detalle (versión y commit) de los mismos análisis que traen las series, para los globos de las gráficas. */
  private consultarDetalles(): void {
    if (this.id == null) {
      return;
    }
    const pedido = this.id;
    this.consultaDetalles?.unsubscribe();
    this.consultaDetalles = this.servicio.historial(pedido, 1, ANALISIS_EN_SERIES).subscribe({
      next: (res) => {
        if (pedido !== this.id || res?.codigoError) {
          return;
        }
        this.detalles = new Map((res?.historial?.analisis ?? []).map((a) => [a.fecha, a]));
        this.armaSeries();
        this.cdr.detectChanges();
      },
      // Sin el detalle, los globos dicen lo demás: fecha, valor, cambio, meta y gate.
      error: () => undefined,
    });
  }

  trackGrafica(_: number, g: GraficaHistorial): string {
    return g.clave;
  }

  /** Abre o cierra los eventos de un renglón que no caben a la vista. */
  alternaEventos(id: number): void {
    const abiertos = new Set(this.eventosAbiertos);
    if (abiertos.has(id)) {
      abiertos.delete(id);
    } else {
      abiertos.add(id);
    }
    this.eventosAbiertos = abiertos;
  }

  eventosVisibles(h: FilaHistorial): string[] {
    return this.eventosAbiertos.has(h.id) ? h.eventos : h.eventos.slice(0, EVENTOS_A_LA_VISTA);
  }

  eventosOcultos(h: FilaHistorial): number {
    return Math.max(0, h.eventos.length - EVENTOS_A_LA_VISTA);
  }

  cambiaPestana(indice: number): void {
    this.pestana = indice;
    if (indice === 0) {
      this.programaResumen();
    }
    if (indice === 1 && !this.historialPedido) {
      this.historialPedido = true;
      this.ajustesSeguidos = 0;
      this.ajusteEncogio = false;
      this.consultarHistorial();
      this.consultarDetalles();
    }
  }

  consultarHistorial(): void {
    if (this.id == null) {
      return;
    }
    const pedido = this.id;
    this.consultaHistorial?.unsubscribe();
    this.historialCargando = true;
    this.historialError = "";
    this.cdr.detectChanges();
    this.consultaHistorial = this.servicio.historial(pedido, this.historialPagina, this.historialPorPagina).subscribe({
      next: (res) => {
        if (pedido !== this.id) {
          return;
        }
        this.historialCargando = false;
        if (res?.codigoError) {
          this.historialError = textoDeError(res, "consultar el historial");
        } else {
          const h = res.historial;
          this.historialTotal = h?.total ?? 0;
          this.historial = (h?.analisis ?? []).map((a) => this.filaHistorial(a));
          // Cada página trae las mismas series: solo si cambiaron se rearman (y las gráficas no repiten su entrada).
          if (res.series && JSON.stringify(res.series) !== JSON.stringify(this.series)) {
            this.series = res.series;
            this.armaSeries();
          }
        }
        this.cdr.detectChanges();
        this.programaAjuste();
      },
      error: () => {
        if (pedido !== this.id) {
          return;
        }
        this.historialCargando = false;
        this.historialError = "No se pudo consultar el historial: sin conexión con el servidor del COS.";
        this.cdr.detectChanges();
      },
    });
  }

  private filaHistorial(a: AnalisisResumen): FilaHistorial {
    const estado = estadoDe(GATE_DEL_ANALISIS, a.estado);
    const gate = this.proyecto?.gateNombre ? `Quality Gate «${this.proyecto.gateNombre}»` : "Quality Gate";
    const cambioGate = (a.eventos ?? []).find((e) => e.categoria === "QUALITY_GATE");
    return {
      id: a.id,
      fecha: fechaHora(a.fecha),
      hace: mayuscula(haceCuanto(a.fecha, this.ahora)),
      estado,
      gateTitulo: veredictoDelGate(estado.valor).replace("Quality Gate", gate),
      gateCambio: cambioGate ? (CAMBIO_DE_GATE[cambioGate.nombre] ?? `cambió: ${cambioGate.nombre}`) : "",
      fallan: estado.valor === "ERROR" ? criteriosFallidos(a.criteriosQueFallan ?? []) : null,
      commit: commitCorto(a.revision),
      commitCompleto: (a.revision ?? "").trim(),
      enlaceCommit: a.enlaceCommit ?? "",
      version: textoVersion(a.versionProyecto),
      eventos: textosDeEventos((a.eventos ?? []).filter((e) => e.categoria !== "QUALITY_GATE"), a.versionProyecto),
      borrado: a.borradoEnSonar === true,
      sinCifrasNuevo: a.cifrasNuevoCompletas === false,
    };
  }

  get paginasHistorial(): number[] {
    return numerosDePagina(this.historialTotal, this.historialPorPagina);
  }

  get rangoHistorial(): string {
    return textoRango(this.historialTotal, this.historialPagina, this.historialPorPagina, "análisis", "análisis");
  }

  irAHistorial(pagina: number): void {
    const tope = this.paginasHistorial.length;
    const destino = Math.min(Math.max(1, pagina), tope);
    if (destino === this.historialPagina) {
      return;
    }
    this.historialPagina = destino;
    this.consultarHistorial();
  }

  @HostListener("window:resize")
  alCambiarTamano(): void {
    if (this.pestana === 0) {
      this.programaResumen();
    }
    if (this.pestana === 1) {
      this.ajustesSeguidos = 0;
      this.ajusteEncogio = false;
      this.programaAjuste();
    }
  }

  /** La tabla vive en una pestaña: se mide a los 250 ms, cuando la pestaña ya tomó su ancho (a los 60 ms aún no). */
  private programaAjuste(): void {
    if (this.esperaAjuste) {
      clearTimeout(this.esperaAjuste);
    }
    this.esperaAjuste = setTimeout(() => {
      this.esperaAjuste = null;
      this.ajustaFilas();
    }, 250);
  }

  /**
   * Las filas del historial que caben hasta el final de la ventana y el aire
   * que se reparte en cada una. Si cambia el aire se repinta y se mide una
   * vez más; si cambian las filas, se vuelve a pedir la página. Las filas por
   * página solo cambian estando en la primera (otra página con renglones más
   * altos ajusta el aire, no el tamaño: si no, brincaría de página) y como
   * mucho tres veces seguidas, por si dos páginas se alternan. Si con la
   * página llena la página rueda (una fila con muchos eventos mide más de lo
   * que la cuenta le concede), se queda con una fila menos.
   */
  private ajustaFilas(pasadas = 2): void {
    const tabla = this.tablaHistorial?.nativeElement;
    let ajuste = ajustePaginado(tabla, MARGEN_HISTORIAL, FILAS_HISTORIAL_MIN, FILAS_HISTORIAL_MAX, ALTO_FILA_HISTORIAL, this.historialTotal);
    let porPagina = cuentaEstable(this.historialPorPagina, ajuste.porPagina, this.ajusteEncogio);
    if (porPagina >= this.historialPorPagina && this.historialRueda(tabla)) {
      porPagina = this.historialPorPagina - 1;
    }
    if (porPagina < ajuste.porPagina) {
      // Se quedan las filas de ahora (la que seguía no cabía): el aire se reparte entre ellas, no entre las propuestas.
      ajuste = ajustePaginado(tabla, MARGEN_HISTORIAL, FILAS_HISTORIAL_MIN, porPagina, ALTO_FILA_HISTORIAL, this.historialTotal);
    }
    if (porPagina !== this.historialPorPagina && this.historialPagina === 1 && this.ajustesSeguidos < 3) {
      this.ajustesSeguidos++;
      this.ajusteEncogio = porPagina < this.historialPorPagina;
      this.historialPorPagina = porPagina;
      this.consultarHistorial();
      return;
    }
    if (ajuste.extraFila !== this.historialExtra) {
      this.historialExtra = ajuste.extraFila;
      this.cdr.detectChanges();
      if (pasadas > 0) {
        this.esperaAjuste = setTimeout(() => {
          this.esperaAjuste = null;
          this.ajustaFilas(pasadas - 1);
        }, 60);
      }
    }
  }

  /**
   * La página rueda con la página del historial llena y sin aire repartido: la última fila no cabía aunque la cuenta
   * dijera que sí. Fuera de la página del COS (sin .main-panel) no se sabe y no rueda.
   */
  private historialRueda(tabla: HTMLElement | undefined): boolean {
    const panel = tabla?.closest(".main-panel");
    const llena = this.historial.length >= this.historialPorPagina && this.historialPorPagina > FILAS_HISTORIAL_MIN;
    return panel != null && llena && this.historialExtra === 0 && panel.scrollHeight > panel.clientHeight + 1;
  }

  trackHistorial(_: number, f: FilaHistorial): number {
    return f.id;
  }

  /** El Resumen se mide cuando ya se pintó (la pestaña y el encabezado ya tomaron su ancho y su alto). */
  private programaResumen(): void {
    if (this.esperaResumen) {
      clearTimeout(this.esperaResumen);
    }
    this.esperaResumen = setTimeout(() => {
      this.esperaResumen = null;
      this.ajustaResumen();
    }, 120);
  }

  /**
   * El alto mínimo del Resumen: de donde empieza (con la página hasta arriba) al final de la ventana, menos el
   * relleno inferior. La letra no crece con este alto: el que sobra lo reparte el aire de cada mosaico
   * (detalle-cc.component.css). Si con la letra normal su contenido pide más (una ventana baja, o el renglón de
   * botones en dos), la letra baja un paso; si ni así cabe, la página rueda sin cortar nada.
   */
  private ajustaResumen(desdeCero = true): void {
    const cuerpo = this.cuerpoResumen?.nativeElement;
    if (!cuerpo) {
      return;
    }
    const panel = cuerpo.closest(".main-panel");
    const arriba = cuerpo.getBoundingClientRect().top + (panel?.scrollTop ?? 0);
    const alto = Math.max(0, Math.floor(window.innerHeight - arriba - MARGEN_RESUMEN));
    // Se mide con la letra normal (desde cero, salvo al cambiar de pestaña) y, si así la pieza pasa del alto, baja un
    // paso. Todo ocurre antes de que el navegador vuelva a pintar: no se alcanza a ver el cambio.
    this.altoResumen = alto;
    if (desdeCero) {
      this.resumenCompacto = false;
    }
    this.cdr.detectChanges();
    if (!this.resumenCompacto && cuerpo.offsetHeight > alto + 1) {
      this.resumenCompacto = true;
      this.cdr.detectChanges();
    }
  }

  /** El ancho final de una cifra en caracteres: su lugar no cambia mientras cuenta (las cifras son tabulares). */
  anchoCifra(f: FilaResumen): number | null {
    return f.cifra ? textoCifra(f.cifra.valor, f.cifra.formato).length : null;
  }

  /* ============================ Reportes ============================ */

  /**
   * «Reporte PDF»: el servidor consulta SonarQube y emite el folio (foto congelada); aquí se arma el PDF con jsPDF.
   * El flotante nombra al proyecto y dice cómo le fue a la consulta (actualizacion-cc.ts).
   */
  pdf(): void {
    const p = this.proyecto;
    if (!p || this.emitiendo) {
      return;
    }
    this.emitiendo = "PDF";
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.reporteProyecto(p.id).subscribe({
      next: (res) => {
        if (res?.codigoError || !res?.reporte) {
          this.terminaEmision(avisoDeError(textoDeError(res, "emitir el reporte")));
          return;
        }
        descargarPdfProyecto(res.reporte);
        this.terminaEmision(avisoDeEmision(`Reporte ${res.reporte.folio} de ${p.nombre}`, res.reporte, "PDF", this.proyecto));
      },
      error: () => this.terminaEmision(avisoDeError(SIN_CONEXION_REPORTE)),
    });
  }

  /** «Excel»: el servidor consulta SonarQube y emite el folio; luego se baja el .xlsx que arma el servidor (POI). */
  excel(): void {
    const p = this.proyecto;
    if (!p || this.emitiendo) {
      return;
    }
    this.emitiendo = "EXCEL";
    this.cdr.detectChanges();
    this.accion?.unsubscribe();
    this.accion = this.servicio.reporteProyecto(p.id).subscribe({
      next: (res) => {
        if (res?.codigoError || !res?.reporte) {
          this.terminaEmision(avisoDeError(textoDeError(res, "emitir el reporte")));
          return;
        }
        this.descargaExcel(res.reporte, p.nombre);
      },
      error: () => this.terminaEmision(avisoDeError(SIN_CONEXION_REPORTE)),
    });
  }

  private descargaExcel(reporte: ReporteEmitido, nombre: string): void {
    const folio = reporte.folio;
    this.accion = this.servicio.reporteExcel(folio).subscribe({
      next: (res) => {
        guardarDescarga(res, `${folio}.xlsx`);
        this.terminaEmision(avisoDeEmision(`Reporte ${folio} de ${nombre}`, reporte, "EXCEL", this.proyecto));
      },
      error: (err: unknown) => {
        void mensajeDeDescarga(err, "descargar el Excel").then((mensaje) => this.terminaEmision(avisoDeError(mensaje)));
      },
    });
  }

  /**
   * Fin de una emisión: el botón vuelve a estar listo, el resultado sale como flotante (éxito, aviso o error) y, si
   * la consulta a SonarQube trajo análisis nuevos o cambios, o si la foto ya no dice lo mismo que el proyecto a la
   * vista (otro análisis, otro veredicto o una cifra que otra pasada guardó después de abrirlo), el proyecto se vuelve
   * a pedir para que diga lo mismo que el reporte.
   */
  private terminaEmision(aviso: AvisoEmisionCc): void {
    this.emitiendo = null;
    this.flotantes[aviso.tipo](aviso.texto);
    this.cdr.detectChanges();
    if (aviso.recargar) {
      this.recarga();
    }
  }

  /** La recarga tras un reporte que trae algo nuevo: el proyecto sin parpadear y, si ya se abrió, el historial con sus globos. */
  private recarga(): void {
    this.consultar(true);
    if (this.historialPedido) {
      this.consultarHistorial();
      this.consultarDetalles();
    }
  }
}

/** " (11 seguidos)", con un espacio que no parte el paréntesis en dos renglones; vacío con uno solo o sin dato. */
function seguidosTexto(n: number | null | undefined): string {
  return n != null && n > 1 ? ` (${entero(n)} seguidos)` : "";
}

/** "desde el 24/02/2026 (11 seguidos)": desde cuándo falla un criterio; vacío sin fecha. */
function desdeTexto(c: Condicion | undefined): string {
  return c?.desde ? `desde el ${fechaCorta(c.desde)}${seguidosTexto(c.analisisSeguidos)}` : "";
}

/** El resultado de un criterio; un estado que no se reconoce se lee como «Sin dato». */
function resultadoDe(estado: EstadoCondicionCc): Resultado {
  const r: Resultado | undefined = RESULTADOS[estado];
  return r ?? RESULTADOS.SIN_DATO;
}

/**
 * "95 % o más", "3 % o menos", "100 %", "0", "A": el umbral de un criterio en
 * palabras, sin los signos ≥ y ≤ (que no cualquiera lee de corrido).
 */
function umbralLlano(c: Pick<Condicion, "metrica" | "operador" | "umbral">): string {
  const tipo = tipoMetrica(c.metrica);
  const n = numero(c.umbral);
  const umbral = textoUmbral(c);
  if (tipo === "LETRA" || n == null || n === 0) {
    return umbral;
  }
  const valor = umbral.replace(/^[≤≥] /, "");
  if (tipo === "PCT" && n === 100 && c.operador === "LT") {
    return valor;
  }
  return `${valor} o ${c.operador === "GT" ? "menos" : "más"}`;
}

/** "Meta: 95 % o más": lo que pide el Quality Gate. */
function metaDe(c: Pick<Condicion, "metrica" | "operador" | "umbral">): string {
  return `Meta: ${umbralLlano(c)}`;
}

/**
 * Un renglón que no se pudo comparar con su meta: «No se evalúa» con el
 * motivo (no hubo código nuevo, no hubo hotspots nuevos), «Sin dato» si el
 * análisis no trajo la cifra, o «Sin meta» si el gate no la pide.
 */
function sinEvaluar(f: FilaResumen, c: Condicion | undefined, motivo: string): FilaResumen {
  if (!c) {
    f.resultado = SIN_META;
  } else {
    f.resultado = motivo ? { texto: "No se evalúa", clase: "is-neutro", icono: "remove", titulo: motivo } : RESULTADOS.SIN_DATO;
  }
  f.falla = false;
  f.desde = "";
  return f;
}

function texto(t: string): Trozo {
  return { texto: t, cifra: false };
}

function cifra(t: string): Trozo {
  return { texto: t, cifra: true };
}

/** Qué abarca «Código general»: el proyecto entero, con sus líneas de código. */
function armaAbarcaGeneral(m: Record<string, string>): Trozo[] {
  const lineas = numero(m["ncloc"]);
  const trozos = [texto("Todo el proyecto")];
  if (lineas != null) {
    trozos.push(texto(" · "), cifra(entero(lineas)), texto(lineas === 1 ? " línea de código" : " líneas de código"));
  }
  return trozos;
}

/** Los textos de los hallazgos por tipo (no cambian con el número): "0 de seguridad", "1 de seguridad". */
const DE_SEGURIDAD: [string, string] = ["de seguridad", "de seguridad"];
const DE_CONFIABILIDAD: [string, string] = ["de confiabilidad", "de confiabilidad"];
const DE_MANTENIBILIDAD: [string, string] = ["de mantenibilidad", "de mantenibilidad"];

/** El enlace de una cifra y lo que abre, para su title: "Abrir en SonarQube los hallazgos nuevos". */
function liga(enlace: string | null | undefined, que: string): Liga {
  return { enlace: enlace ?? "", titulo: `Abrir en SonarQube ${que}` };
}

/**
 * El enlace de una cifra: la lista de SonarQube que manda el servidor o, si un servidor anterior no la manda, la
 * medida del criterio del gate archivo por archivo; sin ninguna, la cifra va sin enlace.
 */
function enlaza(f: FilaResumen, l: Liga, c: Condicion | undefined): void {
  f.enlace = l.enlace || c?.enlace || "";
  f.enlaceTitulo = f.enlace ? l.titulo : "";
}

/** Una cifra chica con su enlace ("48 bloqueantes"); null si el análisis no la trae. */
function enlazado(valor: string | undefined, [singular, pluralTexto]: [string, string], l: Liga): Enlazado | null {
  const n = numero(valor);
  if (n == null) {
    return null;
  }
  return { cifra: entero(n), texto: n === 1 ? singular : pluralTexto, enlace: l.enlace, titulo: l.enlace ? l.titulo : "" };
}

/** "Sobre 8,268 líneas nuevas por cubrir": la base de una cobertura; vacío sin dato. */
function sobre(valor: string | undefined, singular: string, pluralTexto: string): string {
  const n = numero(valor);
  return n == null ? "" : `Sobre ${plural(n, singular, pluralTexto)}`;
}

/** "156 líneas repetidas", "Ninguna línea repetida": la base de una duplicación; vacío sin dato. */
function repetidas(valor: string | undefined): string {
  const n = numero(valor);
  if (n == null) {
    return "";
  }
  return n === 0 ? "Ninguna línea repetida" : plural(n, "línea repetida", "líneas repetidas");
}

/**
 * «Sin evaluar»: el criterio no se evaluó en este análisis aunque su cifra sí llegó (casi siempre porque el Quality
 * Gate cambió después); ni cumple ni deja de cumplir todavía.
 */
function resultadoSinEvaluar(gateCambio: boolean): Resultado {
  return {
    texto: "Sin evaluar",
    clase: "is-neutro",
    icono: "update",
    titulo: gateCambio ? `${POR_GATE_CAMBIADO}: este criterio se evaluará en el próximo` : "SonarQube no evaluó este criterio en este análisis",
  };
}

/** La cifra o la letra se ve (no es la raya de sin dato). */
function conValor(f: FilaResumen): boolean {
  return f.cifra != null || (f.letra !== "" && f.letra !== SIN_DATO);
}

/**
 * Lo que no se evaluó, dicho sin contradecir lo que se ve: si el criterio quedó sin dato pero su cifra (o su letra)
 * sí llegó, dice «Sin evaluar» y por qué; «Sin dato» se queda solo donde no hay cifra. Una parte sin cifra ni meta
 * dice solo «Sin dato», sin la raya junto al «Sin meta».
 */
function aclaraSinEvaluar(hileras: Mosaico[][], gateCambio: boolean): Mosaico[][] {
  const sinEvaluarAun = resultadoSinEvaluar(gateCambio);
  for (const m of hileras.flat()) {
    const filas = m.fila ? [m.fila, ...m.partes] : m.partes;
    for (const f of filas) {
      if (f.resultado === RESULTADOS.SIN_DATO && conValor(f)) {
        f.resultado = sinEvaluarAun;
      }
    }
    for (const p of m.partes) {
      if (p.resultado === SIN_META && (p.valor === SIN_DATO || p.letra === SIN_DATO)) {
        p.valor = "Sin dato";
        p.letra = "";
        p.sinResultado = true;
      }
    }
  }
  return hileras;
}

/** Un mosaico con su cifra grande y lo que la acompaña; va en rojo si no cumple la cifra o alguna de sus partes. */
function mosaico(clave: string, nombre: string, explica: string, fila: FilaResumen | null, extra: Partial<Pick<Mosaico, "unidad" | "sufijo" | "tipos" | "partes">> = {}): Mosaico {
  const partes = extra.partes ?? [];
  return {
    clave,
    nombre,
    explica,
    fila,
    unidad: extra.unidad ?? "",
    sufijo: extra.sufijo ?? "",
    tipos: extra.tipos ?? [],
    partes,
    falla: fila?.falla === true || partes.some((p) => p.falla),
  };
}

/** La pestaña a la que lleva una tecla: las flechas pasan a la otra; Inicio, a la primera; Fin, a la última. */
function destinoDeTecla(tecla: string, actual: VistaResumen): VistaResumen | null {
  switch (tecla) {
    case "ArrowLeft":
    case "ArrowRight":
      return actual === "nuevo" ? "general" : "nuevo";
    case "Home":
      return "nuevo";
    case "End":
      return "general";
    default:
      return null;
  }
}

/**
 * Hace cuánto empezó el código nuevo, en meses de calendario como SonarQube: "hace 7 meses", "hace 1 año", "hace 12
 * días", "hoy"; vacío sin fecha.
 */
function empezoHace(texto: string, ahora: Date): string {
  const desde = aFecha(texto);
  if (!desde) {
    return "";
  }
  let meses = (ahora.getUTCFullYear() - desde.getUTCFullYear()) * 12 + ahora.getUTCMonth() - desde.getUTCMonth();
  if (ahora.getUTCDate() < desde.getUTCDate()) {
    meses--;
  }
  if (meses >= 12) {
    const anios = Math.floor(meses / 12);
    return anios === 1 ? "hace 1 año" : `hace ${anios} años`;
  }
  if (meses >= 1) {
    return meses === 1 ? "hace 1 mes" : `hace ${meses} meses`;
  }
  const dias = Math.floor(Math.max(0, ahora.getTime() - desde.getTime()) / 86_400_000);
  if (dias === 0) {
    return "hoy";
  }
  return dias === 1 ? "hace 1 día" : `hace ${dias} días`;
}

/**
 * "Quality Gate: pasa", "Perfil de calidad: «Sonar way» (java) actualizado con
 * 1 regla nueva": la etiqueta de la categoría y el nombre tal como lo manda el
 * servidor, ya en español. El evento VERSION que solo repite la versión del
 * proyecto (ya va en su propio dato) no se enseña dos veces.
 */
function textosDeEventos(eventos: EventoAnalisis[] | null | undefined, version: string | null | undefined): string[] {
  return (eventos ?? [])
    .filter((e) => !(e.categoria === "VERSION" && version && e.nombre === version))
    .map((e) => `${CATEGORIA_EVENTO[e.categoria] ?? e.categoria}: ${e.nombre}`);
}

/** El estado de la fila de pruebas: falla si alguno de los dos criterios falla; pasa solo si pasan los dos. */
function estadoPruebas(falla: boolean, ambosPasan: boolean): EstadoCondicionCc {
  if (falla) {
    return "ERROR";
  }
  return ambosPasan ? "OK" : "SIN_DATO";
}

/** "Pasó el Quality Gate", "No pasó el Quality Gate" o "Sin resultado del Quality Gate". */
function veredictoDelGate(estado: EstadoGateCc): string {
  switch (estado) {
    case "OK":
      return "Pasó el Quality Gate";
    case "ERROR":
      return "No pasó el Quality Gate";
    default:
      return "Sin resultado del Quality Gate";
  }
}

/** El nombre de un criterio dentro de su grupo, en minúscula: "cobertura", "hallazgos" (los nuevos), "hallazgos abiertos". */
function nombreEnGrupo(metrica: string): string {
  const nombre = metrica === "new_violations" ? "Hallazgos" : nombreMetrica(metrica).replace(/ (del|en el) código nuevo$/, "");
  return nombre.charAt(0).toLowerCase() + nombre.slice(1);
}

/**
 * Los criterios que no cumplió un análisis, en palabras y por grupo, sin
 * repetir (la calificación clásica y la MQR se llaman igual): "Fallaron 3
 * criterios" · "Código nuevo: hallazgos y cobertura" · "Código general:
 * cobertura". Null si no falló ninguno.
 */
function criteriosFallidos(metricas: string[]): CriteriosFallidos | null {
  const grupo = (nuevo: boolean): string[] => [...new Set(metricas.filter((m) => m.startsWith("new_") === nuevo).map(nombreEnGrupo))];
  const nuevos = grupo(true);
  const generales = grupo(false);
  const total = nuevos.length + generales.length;
  if (total === 0) {
    return null;
  }
  const grupos = [
    { nombre: "Código nuevo", lista: nombres(nuevos, nuevos.length) },
    { nombre: "Código general", lista: nombres(generales, generales.length) },
  ].filter((g) => g.lista !== "");
  return { rotulo: total === 1 ? "Falló 1 criterio" : `Fallaron ${entero(total)} criterios`, grupos };
}

/** Las tres gráficas del historial (código general), con su color, su meta y cuánto cambiaron desde el primer análisis. */
function graficasDe(s: SeriesHistorial): GraficaHistorial[] {
  const grafica = (g: Omit<GraficaHistorial, "ultimo" | "cambio">): GraficaHistorial => {
    const serie = { fechas: s.fechas ?? [], valores: g.valores, formato: g.formato, menosEsMejor: g.menosEsMejor };
    return { ...g, ultimo: ultimoValor(g.valores), cambio: cambioDesdeElPrimero(serie) };
  };
  return [
    grafica({
      clave: "cobertura",
      nombre: "Cobertura",
      explica: "Código que revisan las pruebas automáticas",
      tono: "var(--cc-serie-cobertura)",
      valores: s.cobertura ?? [],
      formato: "PCT",
      meta: numero(s.umbrales?.cobertura),
      sentido: "MAS",
      menosEsMejor: false,
      unidad: "",
    }),
    grafica({
      clave: "duplicacion",
      nombre: "Duplicación",
      explica: "Qué parte del código está repetida",
      tono: "var(--cc-serie-duplicacion)",
      valores: s.duplicacion ?? [],
      formato: "PCT",
      meta: numero(s.umbrales?.duplicacion),
      sentido: "MENOS",
      menosEsMejor: true,
      unidad: "",
    }),
    grafica({
      clave: "hallazgos",
      nombre: "Hallazgos abiertos",
      explica: "Problemas que siguen sin resolver",
      tono: "var(--cc-serie-hallazgos)",
      valores: s.hallazgosAbiertos ?? [],
      formato: "ENTERO",
      meta: null,
      sentido: "MENOS",
      menosEsMejor: true,
      unidad: "hallazgos abiertos",
    }),
  ];
}

/** El último valor con dato de una serie, para el encabezado de su gráfica. */
function ultimoValor(valores: (number | null)[]): number | null {
  for (let i = valores.length - 1; i >= 0; i--) {
    const v = valores[i];
    if (v != null) {
      return v;
    }
  }
  return null;
}
